import { Box, Stack, Theme } from "@mui/system";
import { useEffect, useState } from "react";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import {
  CamundaSignalStatus,
  Field,
  Field as IField,
  LoaderState,
} from "@/core/types/types";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import WaitingListStep from "@/core/components/WaitingList/WaitingListStep";

interface CurrentStep {
  index: number;
  label: string;
  waitingTime?: number;
}

const styles = (theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
});

const WaitingList = ({ groupItems }: { groupItems: IField[] }) => {
  const steps: CurrentStep[] = groupItems.map(
    (field: Field, index: number) => ({
      label: field.text!!,
      waitingTime: field.properties.loadingTimeInMs,
      index,
    })
  );

  const [currentStep, setCurrentStep] = useState<CurrentStep>(steps[0]);
  const [processingCompleted, setProcessingCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refreshStep } = useClaimCheck();

  const classes: any = useCustomStyles(styles);

  useEffect(() => {
    const eventName = groupItems.find(
      (field: Field) => field.properties?.signal
    )?.properties?.signal;
    if (!eventName) setError("Signal name is not specified");

    const eventSource = new EventSource(
      `api/camunda/listenToEvent?eventName=${eventName}`
    );
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.status === CamundaSignalStatus.COMPLETED) {
        setProcessingCompleted(true);
        setTimeout(() => refreshStep(), 300);
      } else if (data.status === CamundaSignalStatus.ERROR) {
        setError(data.message);
      }
    };

    return () => {
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStep((prevStep) => {
        if (prevStep.index >= steps.length - 1) {
          clearInterval(timer);
          return prevStep;
        }
        return steps[prevStep.index + 1];
      });
    }, currentStep.waitingTime);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  return (
    <Box sx={classes?.root}>
      <Stack spacing={3}>
        {steps.map((step, index) => {
          const isLoading = index === currentStep.index;
          let stepStatus: LoaderState = isLoading
            ? LoaderState.LOADING
            : index < currentStep.index
            ? LoaderState.DONE
            : LoaderState.WAITING;

          const isLastStep = currentStep.index === steps.length - 1;
          const completeLastStep =
            processingCompleted && isLoading && isLastStep;
          if (completeLastStep) stepStatus = LoaderState.DONE;

          return (
            <WaitingListStep
              key={index}
              label={step.label}
              status={stepStatus}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

export default WaitingList;
